import React, { useEffect, useMemo } from "react";
import { useMeCore } from "../../../cores/me";
import _, { uniqueId } from "lodash";
import { useIntl } from "react-intl";
import { useFacebookCore } from "../../../cores/facebook";
import { useFacebookAdsCore } from "../../../cores/facebookAds";
import { useGoogleCore } from "../../../cores/google";
import { useInstagramCore } from "../../../cores/instagram";
import { useMercadolibreCore } from "../../../cores/mercadolibre";
import { useJumpsellerCore } from "../../../cores/jumpseller";
import { useTiktokCore } from "../../../cores/tiktok";
import { useRouterCore } from "../../../cores/router";
import { isInstagramSocialConnected, isInstagramMessagingConnected } from "../../../cores/socialPost/config";
import { useWooCommerceCore } from "../../../cores/wooCommerce";

import { NavItem2 } from "./NavItem2";
import { NavItemGroup } from "./NavItemGroup";
import { ProtectorCondition } from "../../../cores/globals/interfaces/ProtectorCondition";
import SidebarSubItems, { SidebarSubItem } from "./SidebarSubItems";

export const RenderProtectorRoutes = () => {
  const {
    routes,
    goToSocial,
    goToReports,
    goToActivityBoards,
    goToAutomations,
    goToBinding,
    goToWebsitePages,
  } = useRouterCore();
  const {
    company,
    companyPlan,
    companyRole,
    isAdmin,
    isReseller,
    getMeSocialSettings,
    socialSettings,
  } =
    useMeCore();
  const {
    name: planName,
    rules: {
      allowAnalytics = false,
      allowFacebookAdvertisements = false,
      allowMarket = false,
      allowWebChat = false,
      allowWhatsappChat = false,
      allowActivityBoard = false,
      allowBusinessBoard = false,
      allowAutomation = false,
      allowDatabase = false,
      reportsAmount = 0,
    } = {},
  } = companyPlan! || { name: "." };

  const intl = useIntl();

  const { settings: facebookSettings } = useFacebookCore();
  const { settings: facebookAdsSettings } = useFacebookAdsCore();
  const { settings: googleSettings } = useGoogleCore();
  const { settings: instagramSettings } = useInstagramCore();
  const { settings: wooSettings } = useWooCommerceCore();
  const { settings: mercadolibreSettings } = useMercadolibreCore();
  const { settings: jumpsellerSettings } = useJumpsellerCore();
  const { settings: tiktokSettings } = useTiktokCore();
  const planNotAllowedMessage = `Plan ${
    planName.split(".")[2]
  } no permite esta funcionalidad`;

  const {
    advertisementsSubItems,
    chatSubItems,
    crmSubItems,
    databaseSubItems,
    formsSubItems,
    iaSubItems,
    metricsSubItems,
    emailMarketingSubItems,
    onlineSellsSubItems,
    socialSubItems,
  } = SidebarSubItems({ planNotAllowedMessage });

  const isFacebookSync = !_.isEmpty(facebookSettings);
  const isFacebookAdsSync = !_.isEmpty(facebookAdsSettings);
  const isInstagramSync = isInstagramSocialConnected(socialSettings);
  const isGoogleSync = !_.isEmpty(googleSettings);
  const isWooSync = !_.isEmpty(wooSettings);
  const isMercadolibreSync = !_.isEmpty(mercadolibreSettings);
  const isJumpsellerSync = !_.isEmpty(jumpsellerSettings);
  const isTikTokSync = !_.isEmpty(tiktokSettings);
  const hasAnyOnlineSalesIntegration =
    isWooSync || isMercadolibreSync || isJumpsellerSync;
  const isReportAllow = reportsAmount > 0;

  // Ensure social settings are hydrated when sidebar renders.
  useEffect(() => {
    if (company?.id) {
      getMeSocialSettings(company.id);
    }
  }, [company?.id, getMeSocialSettings]);

  const isInstagramMessagingSync = isInstagramMessagingConnected(
    socialSettings as Record<string, unknown> | undefined
  );
  const allowChat =
    allowWebChat ||
    allowWhatsappChat ||
    isInstagramMessagingSync ||
    isFacebookSync;

  const metricsActive =
    company != null &&
    (allowAnalytics ||
      isFacebookSync ||
      isInstagramSync ||
      isTikTokSync ||
      isGoogleSync ||
      isMercadolibreSync ||
      isWooSync ||
      isReportAllow);

  const standardRoles = useMemo(() => {
    return (
      isReseller ||
      isAdmin ||
      companyRole?.owner === true ||
      companyRole?.admin === true ||
      companyRole?.agent === true
    );
  }, [
    isReseller,
    isAdmin,
    companyRole?.owner,
    companyRole?.admin,
    companyRole?.agent,
  ]);

  const protectorRoutes: ProtectorCondition[] = [
    {
      moduleIsActive: true,
      condition: company != null,
      render: (
        <>
          <NavItem2
            onClick={goToSocial}
            label={intl.formatMessage({ id: "app.sidebar.home" })}
            iconName="home"
            activeUrl={routes.social}
          />
        </>
      ),
    },
    {
      moduleIsActive: true,
      condition: company != null,
      render: (
        <NavItem2
          onClick={goToBinding}
          label={intl.formatMessage({ id: "app.navbar.integrations" })}
          iconName="circle-nodes"
          activeUrl={routes.binding}
        />
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null &&
        (standardRoles ||
          companyRole?.social === true ||
          companyRole?.ads === true),
      render: (
        <NavItemGroup
          groupId={`social`}
          label={intl.formatMessage({ id: "app.sidebar.socialMedia" })}
          iconName="thumbs-up"
          activeUrls={[
            routes.socialPost,
            routes.socialAutomatedPosts,
            // routes.socialStory, // Comentado - Stories no funcional
            routes.socialHistory,
            routes.socialContentGrid,
            routes.socialCalendar,
            routes.facebookCampaigns,
            routes.createCampaign,
          ]}
        >
          {socialSubItems.map(
            ({
              activeUrl,
              iconName,
              label,
              onClick,
              icon,
              iconPrefix,
              disabled,
              disabledMessage,
            }: SidebarSubItem) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                iconName={iconName}
                icon={icon}
                iconPrefix={iconPrefix}
                disabled={disabled}
                disabledMessage={disabledMessage}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition: company != null,
      render: (
        <NavItemGroup
          groupId={`ia`}
          label={intl.formatMessage({ id: "app.sidebar.ia" })}
          iconName="wand-magic-sparkles"
          activeUrls={[routes.videoGen, routes.videoEditor, routes.aiGeneration]}
        >
          {iaSubItems.map(
            ({
              activeUrl,
              iconName,
              label,
              onClick,
              iconPrefix,
              disabled,
              disabledMessage,
            }: SidebarSubItem) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                iconName={iconName}
                iconPrefix={iconPrefix}
                disabled={disabled}
                disabledMessage={disabledMessage}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null &&
        (standardRoles || companyRole?.ads === true) &&
        advertisementsSubItems.length > 0,
      render: (
        <NavItemGroup
          groupId={`advertisements`}
          label={intl.formatMessage({ id: "app.sidebar.advertisements" })}
          iconName="bullhorn"
          activeUrls={[routes.facebookCampaigns, routes.createCampaign]}
          disabled={!allowFacebookAdvertisements || !isFacebookAdsSync}
          disabledMessage={planNotAllowedMessage}
        >
          {advertisementsSubItems.map(
            ({ activeUrl, iconName, label, onClick, disabled }) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                iconName={iconName}
                disabled={disabled}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null && (standardRoles || companyRole?.forms === true),
      render: (
        <NavItemGroup
          groupId={`forms`}
          label={intl.formatMessage({ id: "app.sidebar.forms" })}
          iconName="wpforms"
          iconPrefix="fab"
          activeUrls={[routes.webForms, routes.facebookForm]}
        >
          {formsSubItems.map(
            ({ activeUrl, iconName, label, onClick, disabled, iconPrefix }) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                iconName={iconName}
                iconPrefix={iconPrefix}
                disabled={disabled}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null &&
        (standardRoles || companyRole?.forms === true) &&
        (companyPlan?.rules.websitePagesAmount || 0) > 0,
      render: (
        <NavItem2
          activeUrl={routes.websitePages}
          onClick={goToWebsitePages}
          label={intl.formatMessage({ id: "app.sidebar.websitePage" })}
          iconName="globe"
        />
      ),
    },
    {
      moduleIsActive: true,
      condition: Boolean(isAdmin && company),
      render: (
        <NavItemGroup
          groupId="emailMarketing"
          label={intl.formatMessage({ id: "app.sidebar.emailMarketing" })}
          iconName="envelope"
          activeUrls={[
            routes.emailMarketingLists,
            routes.emailMarketingEmails,
            routes.emailMarketingCampaignDetail,
            routes.emailMarketingTemplates,
            routes.emailMarketingReports,
            routes.emailMarketingConfig,
          ]}
        >
          {emailMarketingSubItems.map(
            ({ activeUrl, iconName, label, onClick }) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                iconName={iconName}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null && (standardRoles || companyRole?.chat === true),
      render: (
        <NavItemGroup
          groupId={`comments`}
          label={intl.formatMessage({ id: "app.sidebar.chat" })}
          iconName="comments"
          activeUrls={[
            routes.webChat,
            routes.editWebChat,
            routes.editWhatsappChat,
          ]}
          disabled={!allowChat}
          disabledMessage={planNotAllowedMessage}
        >
          {chatSubItems.map(
            ({
              activeUrl,
              iconPrefix,
              iconName,
              label,
              onClick,
              disabled,
              disabledMessage,
            }) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                iconPrefix={iconPrefix}
                iconName={iconName}
                disabled={disabled}
                disabledMessage={disabledMessage}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null && (standardRoles || companyRole?.activities === true),
      render: (
        <NavItem2
          onClick={goToActivityBoards}
          label={intl.formatMessage({ id: "app.sidebar.activities" })}
          iconName="columns"
          activeUrl={routes.activityBoards}
          disabled={!allowActivityBoard}
          disabledMessage={planNotAllowedMessage}
        />
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null &&
        (standardRoles || companyRole?.market === true) &&
        hasAnyOnlineSalesIntegration,
      render: (
        <NavItemGroup
          groupId={`onlineSellers`}
          label={intl.formatMessage({ id: "app.sidebar.OnlineSales" })}
          iconName="store"
          activeUrls={[
            routes.wooCommerceOrders,
            routes.mercadolibreOrders,
            routes.jumpsellerOrders,
          ]}
          disabled={!allowMarket}
          disabledMessage={planNotAllowedMessage}
        >
          {onlineSellsSubItems.map(
            ({
              activeUrl,
              label,
              onClick,
              disabled,
              icon,
              iconName,
              iconPrefix,
            }) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                icon={icon}
                iconName={iconName}
                iconPrefix={iconPrefix}
                disabled={disabled}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null && (standardRoles || companyRole?.crm === true),
      render: (
        <NavItemGroup
          groupId={`crm`}
          label={intl.formatMessage({ id: "app.sidebar.crm" })}
          iconName="funnel-dollar"
          disabled={!allowBusinessBoard}
          disabledMessage={planNotAllowedMessage}
          activeUrls={[
            routes.businessBoard,
            routes.tasks,
            routes.businessBoardAnalytics,
            routes.calendar,
          ]}
        >
          {crmSubItems.map(({ activeUrl, label, onClick, iconName }) => (
            <NavItem2
              key={uniqueId()}
              onClick={onClick}
              label={label}
              iconName={iconName}
              activeUrl={activeUrl}
            />
          ))}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null && (standardRoles || companyRole?.contacts === true),
      render: (
        <NavItemGroup
          groupId={`database`}
          label={intl.formatMessage({ id: "app.sidebar.database" })}
          iconName="database"
          activeUrls={[routes.contacts, routes.contactCompanies]}
          disabled={!allowDatabase}
          disabledMessage={planNotAllowedMessage}
        >
          {databaseSubItems.map(({ activeUrl, label, onClick, iconName }) => (
            <NavItem2
              key={uniqueId()}
              activeUrl={activeUrl}
              onClick={onClick}
              label={label}
              iconName={iconName}
            />
          ))}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        metricsActive &&
        (standardRoles ||
          companyRole?.analitycs === true ||
          companyRole?.reports === true),
      render: (
        <NavItemGroup
          groupId={`metrics`}
          label={intl.formatMessage({ id: "app.sidebar.metrics" })}
          iconName="chart-area"
          activeUrls={[
            routes.reports,
            routes.googleAnalytics,
            routes.facebookAnalytics,
            routes.instagramAnalytics,
            routes.tiktokAnalytics,
            routes.wooCommerceAnalytics,
            routes.mercadolibreAnalytics,
          ]}
          disabled={!allowAnalytics}
          disabledMessage={planNotAllowedMessage}
        >
          <NavItem2
            onClick={goToReports}
            label={intl.formatMessage({ id: "app.sidebar.reports" })}
            iconName="file"
            activeUrl={routes.reports}
            disabled={!isReportAllow}
          />
          {metricsSubItems.map(
            ({
              activeUrl,
              label,
              onClick,
              disabled,
              icon,
              iconName,
              iconPrefix,
            }) => (
              <NavItem2
                key={uniqueId()}
                activeUrl={activeUrl}
                onClick={onClick}
                label={label}
                icon={icon}
                iconName={iconName}
                iconPrefix={iconPrefix}
                disabled={disabled}
              />
            )
          )}
        </NavItemGroup>
      ),
    },
    {
      moduleIsActive: true,
      condition:
        company != null && (standardRoles || companyRole?.automations === true),
      render: (
        <NavItem2
          onClick={goToAutomations}
          label={intl.formatMessage({ id: "app.sidebar.automations" })}
          iconName="robot"
          disabled={!allowAutomation}
          activeUrl={routes.automations}
        />
      ),
    },
  ];
  return { protectorRoutes };
};
